import { useState, useCallback } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2, Radio, ShieldCheck } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { SourceForm } from "./components/source-form"
import { SourcesQuery } from "@/lib/graphql/queries"
import { DeleteSourceMutation, BulkDeleteSourcesMutation } from "@/lib/graphql/mutations"
import { SourceChangedSubscription } from "@/lib/graphql/subscriptions"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"
import type { SourceStatus } from "@/lib/generated/graphql"

type SourceItem = ResultOf<typeof SourcesQuery>["sources"]["nodes"][number]

const LIMIT = 25

export function SourcesPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const status = (searchParams.get("status") as SourceStatus) || undefined
  const providerType = searchParams.get("providerType") ?? undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [status, providerType, search])

  const [result, reexecute] = useQuery({
    query: SourcesQuery,
    variables: { status, providerType, search, after: pagination.after, first: pagination.first },
  })
  const handleSourceChanged = useCallback(
    (
      _prev: ResultOf<typeof SourceChangedSubscription> | undefined,
      response: ResultOf<typeof SourceChangedSubscription>,
    ) => {
      if (response?.sourceChanged) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: SourceChangedSubscription, pause: !isConfigured }, handleSourceChanged)

  const [, deleteSource] = useMutation(DeleteSourceMutation)
  const [, bulkDeleteSources] = useMutation(BulkDeleteSourcesMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const columns: Column<SourceItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Slug",
      cell: (row) => <code className="text-xs font-mono text-muted-foreground">{row.slug}</code>,
    },
    {
      header: "Provider",
      cell: (row) => (
        <span className="text-xs capitalize flex items-center gap-1.5">
          {row.providerType}
          {row.brokerConfig && (
            <Radio className="h-3 w-3 text-primary" aria-label="Broker source" />
          )}
        </span>
      ),
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Rate Limit",
      cell: (row) => (
        <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1.5">
          {row.rateLimitRps > 0 ? `${row.rateLimitRps} rps` : "unlimited"}
          {row.spikeProtection && (
            <ShieldCheck className="h-3 w-3 text-primary" aria-label="Spike protection enabled" />
          )}
        </span>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteSource({ id: deleteId })
    setDeleting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("Source deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteSources({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success(`Deleted ${res.data?.bulkDeleteSources ?? 0} sources`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Sources" description="Webhook ingestion endpoints">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Source
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={providerType ?? "all"}
          onValueChange={(v) => setFilter("providerType", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All providers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All providers</SelectItem>
            <SelectItem value="github">GitHub</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="twilio">Twilio</SelectItem>
            <SelectItem value="generic">Generic</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search sources..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.sources.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.sources.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No sources"
        emptyDescription="Create a source to start receiving webhooks"
        onRowClick={(row) => navigate(`/sources/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <SourceForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Source"
        description="This will permanently delete this source and all associated data."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Sources"
        description={`This will permanently delete ${selectedKeys.size} selected sources and all associated data.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
